/**
 * YouTube Gallery — Client-side
 * Fetches all channel videos via YouTube Data API v3,
 * renders a sortable/searchable grid with lightbox player.
 */

(function () {
  'use strict';

  const CACHE_KEY = 'yt_gallery_cache';
  const CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours

  let apiKey = '';
  let channelHandle = '';
  let allVideos = [];
  let filteredVideos = [];
  let channelStats = null;
  let sortMode = 'newest';
  let searchQuery = '';
  let container = null;

  /* ── Public init ── */
  function init(key, handle) {
    apiKey = key;
    channelHandle = handle || '@BentwoodRingSupplies';
    container = document.getElementById('yt-gallery');
    if (!container) return;

    setupControls();

    const cached = getCache();
    if (cached) {
      allVideos = cached.videos || cached; // back-compat
      channelStats = cached.stats || null;
      renderStats();
      applyFilterAndRender();
      // Background refresh
      fetchAllVideos().then(function (result) {
        allVideos = result.videos;
        channelStats = result.stats;
        setCache(result);
        renderStats();
        applyFilterAndRender();
      }).catch(function () { /* cached data still showing */ });
    } else {
      showLoading();
      fetchAllVideos().then(function (result) {
        allVideos = result.videos;
        channelStats = result.stats;
        setCache(result);
        renderStats();
        applyFilterAndRender();
      }).catch(function (err) {
        showError(err.message || 'Failed to load videos');
      });
    }
  }

  /* ── YouTube Data API helpers ── */
  function ytApi(endpoint, params) {
    var url = 'https://www.googleapis.com/youtube/v3/' + endpoint + '?key=' + encodeURIComponent(apiKey);
    Object.keys(params).forEach(function (k) {
      url += '&' + encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
    });
    return fetch(url).then(function (res) {
      if (!res.ok) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          throw new Error(body.error ? body.error.message : 'API error ' + res.status);
        });
      }
      return res.json();
    });
  }

  function fetchAllVideos() {
    // Step 1: Resolve channel handle → uploads playlist ID + stats
    return ytApi('channels', {
      forHandle: channelHandle,
      part: 'contentDetails,snippet,statistics'
    }).then(function (channelRes) {
      if (!channelRes.items || !channelRes.items.length) {
        throw new Error('Channel not found for handle: ' + channelHandle);
      }
      var ch = channelRes.items[0];
      var uploadsId = ch.contentDetails.relatedPlaylists.uploads;

      // Capture channel stats
      var stats = ch.statistics || {};
      channelStats = {
        subscriberCount: parseInt(stats.subscriberCount || '0', 10),
        hiddenSubscriberCount: !!stats.hiddenSubscriberCount,
        videoCount: parseInt(stats.videoCount || '0', 10),
        viewCount: parseInt(stats.viewCount || '0', 10),
        title: ch.snippet ? ch.snippet.title : '',
        thumbnail: ch.snippet && ch.snippet.thumbnails ?
          (ch.snippet.thumbnails.medium ? ch.snippet.thumbnails.medium.url :
           ch.snippet.thumbnails.default ? ch.snippet.thumbnails.default.url : '') : ''
      };

      // Step 2: Fetch all playlist items (paginate)
      return fetchPlaylistPages(uploadsId, '', []);
    }).then(function (items) {
      // Step 3: Fetch video details (duration, views) in batches of 50
      var videoIds = items.map(function (item) {
        return item.snippet.resourceId.videoId;
      });
      return fetchVideoDetails(videoIds).then(function (detailsMap) {
        return items.map(function (item) {
          var s = item.snippet;
          var vid = s.resourceId.videoId;
          var d = detailsMap[vid] || {};
          return {
            id: vid,
            title: s.title,
            description: s.description || '',
            thumbnail: (s.thumbnails && (
              (s.thumbnails.maxres && s.thumbnails.maxres.url) ||
              (s.thumbnails.high && s.thumbnails.high.url) ||
              (s.thumbnails.medium && s.thumbnails.medium.url) ||
              (s.thumbnails.default && s.thumbnails.default.url)
            )) || '',
            publishedAt: s.publishedAt,
            duration: d.duration || '',
            viewCount: d.viewCount || 0,
            likeCount: d.likeCount || 0
          };
        }).filter(function (v) {
          return v.title !== 'Private video' && v.title !== 'Deleted video';
        });
      });
    }).then(function (videos) {
      return { videos: videos, stats: channelStats };
    });
  }

  function fetchPlaylistPages(playlistId, pageToken, accumulated) {
    var params = {
      playlistId: playlistId,
      part: 'snippet',
      maxResults: '50'
    };
    if (pageToken) params.pageToken = pageToken;

    return ytApi('playlistItems', params).then(function (res) {
      var items = accumulated.concat(res.items || []);
      if (res.nextPageToken) {
        return fetchPlaylistPages(playlistId, res.nextPageToken, items);
      }
      return items;
    });
  }

  function fetchVideoDetails(videoIds) {
    var detailsMap = {};
    var batches = [];
    for (var i = 0; i < videoIds.length; i += 50) {
      batches.push(videoIds.slice(i, i + 50));
    }

    return batches.reduce(function (chain, batch) {
      return chain.then(function () {
        return ytApi('videos', {
          id: batch.join(','),
          part: 'contentDetails,statistics'
        }).then(function (res) {
          (res.items || []).forEach(function (v) {
            detailsMap[v.id] = {
              duration: v.contentDetails.duration,
              viewCount: parseInt(v.statistics.viewCount || '0', 10),
              likeCount: parseInt(v.statistics.likeCount || '0', 10)
            };
          });
        });
      });
    }, Promise.resolve()).then(function () {
      return detailsMap;
    });
  }

  /* ── Sorting & filtering ── */
  function applyFilterAndRender() {
    var q = searchQuery.toLowerCase().trim();
    filteredVideos = allVideos.filter(function (v) {
      if (!q) return true;
      return v.title.toLowerCase().indexOf(q) !== -1 ||
             v.description.toLowerCase().indexOf(q) !== -1;
    });

    filteredVideos.sort(function (a, b) {
      switch (sortMode) {
        case 'oldest':
          return (a.publishedAt || '').localeCompare(b.publishedAt || '');
        case 'popular':
          return b.viewCount - a.viewCount;
        case 'az':
          return a.title.localeCompare(b.title);
        default: // newest
          return (b.publishedAt || '').localeCompare(a.publishedAt || '');
      }
    });

    renderGrid();
  }

  function setSort(mode) {
    sortMode = mode;
    container.querySelectorAll('.yt-sort-btn').forEach(function (btn) {
      btn.classList.toggle('yt-sort-active', btn.dataset.sort === mode);
    });
    applyFilterAndRender();
  }

  function setSearch(query) {
    searchQuery = query;
    var clearBtn = container.querySelector('.yt-search-clear');
    if (clearBtn) clearBtn.classList.toggle('yt-visible', query.length > 0);
    applyFilterAndRender();
  }

  function setupControls() {
    // Sort buttons
    container.querySelectorAll('.yt-sort-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setSort(btn.dataset.sort);
      });
    });

    // Search input
    var searchInput = container.querySelector('.yt-search');
    if (searchInput) {
      var debounceTimer;
      searchInput.addEventListener('input', function () {
        clearTimeout(debounceTimer);
        var val = searchInput.value;
        debounceTimer = setTimeout(function () { setSearch(val); }, 200);
      });
    }

    // Search clear button
    var clearBtn = container.querySelector('.yt-search-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        var input = container.querySelector('.yt-search');
        if (input) input.value = '';
        setSearch('');
      });
    }

    // Modal close on overlay click
    var overlay = document.querySelector('.yt-modal-overlay');
    if (overlay) {
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) closeModal();
      });
    }

    // Modal close button
    var closeBtn = document.querySelector('.yt-modal-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }

    // Escape key closes modal
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });
  }

  /* ── Render channel stats badge ── */
  function renderStats() {
    var statsEl = container.querySelector('.yt-stats');
    if (!statsEl || !channelStats) return;

    var subs = channelStats.hiddenSubscriberCount
      ? null
      : formatViews(channelStats.subscriberCount);

    var html = '';
    if (subs) {
      html +=
        '<div class="yt-stat yt-stat--primary">' +
          '<svg class="yt-stat-icon" viewBox="0 0 24 24"><path d="M21.58 7.19c-.23-.86-.91-1.54-1.77-1.77C18.25 5 12 5 12 5s-6.25 0-7.81.42c-.86.23-1.54.91-1.77 1.77C2 8.75 2 12 2 12s0 3.25.42 4.81c.23.86.91 1.54 1.77 1.77C5.75 19 12 19 12 19s6.25 0 7.81-.42c.86-.23 1.54-.91 1.77-1.77C22 15.25 22 12 22 12s0-3.25-.42-4.81zM10 15V9l5.2 3-5.2 3z"/></svg>' +
          '<div class="yt-stat-text">' +
            '<div class="yt-stat-value">' + subs + '</div>' +
            '<div class="yt-stat-label">subscribers</div>' +
          '</div>' +
        '</div>';
    }

    if (channelStats.videoCount || channelStats.viewCount) {
      html += '<div class="yt-stat-row">';
      if (channelStats.videoCount) {
        html +=
          '<div class="yt-stat-mini">' +
            '<span class="yt-stat-mini-value">' + channelStats.videoCount.toLocaleString() + '</span>' +
            '<span class="yt-stat-mini-label">videos</span>' +
          '</div>';
      }
      if (channelStats.viewCount) {
        html +=
          '<div class="yt-stat-mini">' +
            '<span class="yt-stat-mini-value">' + formatViews(channelStats.viewCount) + '</span>' +
            '<span class="yt-stat-mini-label">total views</span>' +
          '</div>';
      }
      html += '</div>';
    }

    statsEl.innerHTML = html;
    statsEl.classList.add('yt-stats-visible');
  }

  /* ── Rendering ── */
  function renderGrid() {
    var grid = container.querySelector('.yt-grid');
    var loadingEl = container.querySelector('.yt-loading');
    var emptyEl = container.querySelector('.yt-empty');
    var countEl = container.querySelector('.yt-results-count');

    if (loadingEl) loadingEl.style.display = 'none';

    if (filteredVideos.length === 0) {
      grid.innerHTML = '';
      if (emptyEl) {
        emptyEl.textContent = searchQuery
          ? 'No videos match "' + escHtml(searchQuery) + '"'
          : 'No videos found.';
        emptyEl.style.display = 'block';
      }
      if (countEl) countEl.style.display = 'none';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';

    // Show count when searching
    if (countEl) {
      if (searchQuery) {
        countEl.textContent = filteredVideos.length + ' video' + (filteredVideos.length !== 1 ? 's' : '') + ' found';
        countEl.style.display = 'block';
      } else {
        countEl.textContent = allVideos.length + ' videos';
        countEl.style.display = 'block';
      }
    }

    grid.innerHTML = '';
    filteredVideos.forEach(function (video) {
      grid.appendChild(createCard(video));
    });
  }

  function createCard(video) {
    var card = document.createElement('div');
    card.className = 'yt-card';

    var duration = formatDuration(video.duration);
    var date = formatDate(video.publishedAt);
    var views = formatViews(video.viewCount);

    var shortDesc = truncate(video.description, 180);

    card.innerHTML =
      '<div class="yt-card-thumb">' +
        '<img src="' + escAttr(video.thumbnail) + '" alt="' + escAttr(video.title) + '" loading="lazy">' +
        '<div class="yt-play-overlay">' +
          '<div class="yt-play-btn">' +
            '<svg viewBox="0 0 24 24"><polygon points="8,5 20,12 8,19"/></svg>' +
          '</div>' +
        '</div>' +
        (duration ? '<span class="yt-duration">' + duration + '</span>' : '') +
      '</div>' +
      '<div class="yt-card-body">' +
        '<h3 class="yt-card-title">' + escHtml(video.title) + '</h3>' +
        (shortDesc ? '<p class="yt-card-desc">' + escHtml(shortDesc) + '</p>' : '') +
        '<div class="yt-card-meta">' +
          '<span>' +
            '<svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>' +
            views +
          '</span>' +
          '<span>' +
            '<svg viewBox="0 0 24 24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/></svg>' +
            date +
          '</span>' +
        '</div>' +
      '</div>';

    card.addEventListener('click', function () {
      openModal(video);
    });

    return card;
  }

  /* ── Lightbox modal ── */
  function openModal(video) {
    var overlay = document.querySelector('.yt-modal-overlay');
    var player = document.querySelector('.yt-modal-player');
    var title = document.querySelector('.yt-modal-title');
    var meta = document.querySelector('.yt-modal-meta');
    var actions = document.querySelector('.yt-modal-actions');
    var desc = document.querySelector('.yt-modal-desc');

    if (!overlay || !player) return;

    player.innerHTML = '<iframe src="https://www.youtube.com/embed/' +
      encodeURIComponent(video.id) +
      '?autoplay=1&rel=0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>';

    if (title) title.textContent = video.title;
    if (meta) {
      meta.textContent = formatViews(video.viewCount) + ' views \u00B7 ' + formatDate(video.publishedAt);
    }

    var watchUrl = 'https://www.youtube.com/watch?v=' + video.id;

    if (actions) {
      actions.innerHTML =
        '<a href="' + escAttr(watchUrl) + '" target="_blank" rel="noopener" class="yt-modal-btn">' +
          '<svg viewBox="0 0 24 24"><path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3z"/></svg>' +
          'Open on YouTube' +
        '</a>' +
        '<button type="button" class="yt-modal-btn" data-copy-url="' + escAttr(watchUrl) + '">' +
          '<svg viewBox="0 0 24 24"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>' +
          '<span class="yt-copy-label">Copy link</span>' +
        '</button>';

      var copyBtn = actions.querySelector('[data-copy-url]');
      if (copyBtn) {
        copyBtn.addEventListener('click', function () {
          copyToClipboard(watchUrl);
          copyBtn.classList.add('yt-copied');
          var label = copyBtn.querySelector('.yt-copy-label');
          if (label) label.textContent = 'Copied!';
          setTimeout(function () {
            copyBtn.classList.remove('yt-copied');
            if (label) label.textContent = 'Copy link';
          }, 1800);
        });
      }
    }

    if (desc) {
      desc.innerHTML = linkify(escHtml(video.description || ''));
    }

    overlay.classList.add('yt-modal-open');
    document.body.style.overflow = 'hidden';
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(function () { fallbackCopy(text); });
    } else {
      fallbackCopy(text);
    }
  }

  function fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);
  }

  function linkify(html) {
    return html.replace(/(https?:\/\/[^\s<]+)/g, function (url) {
      return '<a href="' + url + '" target="_blank" rel="noopener">' + url + '</a>';
    });
  }

  function truncate(str, n) {
    if (!str) return '';
    var clean = str.replace(/\s+/g, ' ').trim();
    if (clean.length <= n) return clean;
    return clean.substring(0, n).replace(/\s+\S*$/, '') + '\u2026';
  }

  function closeModal() {
    var overlay = document.querySelector('.yt-modal-overlay');
    var player = document.querySelector('.yt-modal-player');
    var actions = document.querySelector('.yt-modal-actions');
    var desc = document.querySelector('.yt-modal-desc');

    if (!overlay) return;
    overlay.classList.remove('yt-modal-open');
    document.body.style.overflow = '';

    // Stop video playback & clear modal contents
    if (player) player.innerHTML = '';
    if (actions) actions.innerHTML = '';
    if (desc) desc.innerHTML = '';
  }

  /* ── UI states ── */
  function showLoading() {
    var el = container.querySelector('.yt-loading');
    if (el) el.style.display = 'block';
    var grid = container.querySelector('.yt-grid');
    if (grid) grid.innerHTML = '';
    var empty = container.querySelector('.yt-empty');
    if (empty) empty.style.display = 'none';
    var count = container.querySelector('.yt-results-count');
    if (count) count.style.display = 'none';
  }

  function showError(msg) {
    var loadingEl = container.querySelector('.yt-loading');
    if (loadingEl) loadingEl.style.display = 'none';

    var grid = container.querySelector('.yt-grid');
    if (grid) {
      grid.innerHTML = '<div class="yt-error">' +
        '<strong>Unable to load videos</strong><br>' +
        escHtml(msg) +
        '</div>';
    }
  }

  /* ── Cache ── */
  function getCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (Date.now() - data.ts > CACHE_TTL) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      // Support both old (videos array) and new ({videos, stats}) format
      if (Array.isArray(data.videos)) {
        return { videos: data.videos, stats: data.stats || null };
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  function setCache(result) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        ts: Date.now(),
        videos: result.videos,
        stats: result.stats
      }));
    } catch (e) { /* quota exceeded — ignore */ }
  }

  /* ── Formatting helpers ── */
  function formatDuration(iso) {
    if (!iso) return '';
    var match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return '';
    var h = parseInt(match[1] || '0', 10);
    var m = parseInt(match[2] || '0', 10);
    var s = parseInt(match[3] || '0', 10);

    if (h > 0) {
      return h + ':' + pad(m) + ':' + pad(s);
    }
    return m + ':' + pad(s);
  }

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function formatDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
  }

  function formatViews(count) {
    if (!count && count !== 0) return '';
    if (count >= 1000000) return (count / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (count >= 1000) return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return count.toLocaleString();
  }

  function escHtml(str) {
    var d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  function escAttr(str) {
    return (str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ── Expose ── */
  window.YouTubeGallery = { init: init };
})();
